import { useState } from 'react';
import { money } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import {
  useAccountLedger,
  useFiscalYears,
  useTrialBalance,
  type TrialBalanceRow,
} from '../lib/accounting-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * MİZAN VE DEFTER-İ KEBİR.
 *
 * ⚠ MİZANDA `POSTED` **VE** `REVERSED` fişler vardır; yalnız `DRAFT` yoktur.
 * `REVERSED` "geçersiz" demez — karşısına ters kayıt atılmış demektir ve ters
 * kaydın kendisi `POSTED`tır; ikisi birlikte sıfırlanır. Yalnız `POSTED`
 * süzülseydi mizan tam ters kayıt tutarı kadar kayar ve — en kötüsü — yine
 * DENGELİ görünürdü.
 *
 * ⚠ DENKLİK UYARISI GİZLENMEZ. Mizanın denk olmaması, her fiş kendi içinde
 * dengeliyken bile mümkündür; bunu sessizce basmak, yanlış bir bilançoyu doğru
 * sandırırdı.
 */

const SIGN_HINT: Record<TrialBalanceRow['type'], string> = {
  ASSET: 'borç bakiyesi beklenir',
  EXPENSE: 'borç bakiyesi beklenir',
  LIABILITY: 'alacak bakiyesi beklenir',
  EQUITY: 'alacak bakiyesi beklenir',
  INCOME: 'alacak bakiyesi beklenir',
};

export function TrialBalancePage() {
  const years = useFiscalYears();
  const [year, setYear] = useState<number | null>(null);
  const [asOf, setAsOf] = useState('');
  const [openAccount, setOpenAccount] = useState<string | null>(null);

  // ⚠ Yıl listesi gelince ilk (en yeni) yıl seçilir; boş bir mizan ekranı,
  //   operatöre "kayıt yok" izlenimi verirdi.
  const effectiveYear = year ?? years.data?.[0]?.year ?? null;

  const tb = useTrialBalance(effectiveYear, asOf || undefined);

  return (
    <>
      <PageHeader
        title="Mizan"
        description="Hesap bazında borç ve alacak toplamları. Ters çevrilmiş fişler de defterdedir — ters kayıtla birlikte sıfırlanırlar."
      />

      <Card className="mt-4">
        <div className="flex flex-wrap gap-2 px-4 py-3">
          <Select
            label="Mali yıl"
            value={effectiveYear === null ? '' : String(effectiveYear)}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {(years.data ?? []).map((y) => (
              <option key={y.year} value={y.year}>
                {y.year}
              </option>
            ))}
          </Select>
          <Input
            label="Şu tarihe kadar"
            type="date"
            value={asOf}
            hint="Boş bırakılırsa yılın tamamı"
            onChange={(e) => setAsOf(e.target.value)}
          />
        </div>

        {tb.isPending && effectiveYear !== null ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : tb.isError ? (
          <ErrorState message={errorMessage(tb.error)} />
        ) : !tb.data || tb.data.rows.length === 0 ? (
          <EmptyState
            title="Hareket yok"
            description="Bu mali yılda deftere atılmış fiş bulunmuyor."
          />
        ) : (
          <>
            {/*
              ⚠ DENKLİK UYARISI EN ÜSTTE. Mizan denk değilse rakamların hiçbiri
              güvenilir değildir; sayfanın altına gömülü bir rozet, operatörün
              göremeyeceği tek yerdir.
            */}
            {!tb.data.balanced && (
              <div className="mx-4 mb-2 rounded-[var(--r-md)] border border-[var(--c-danger)] px-3 py-2">
                <p className="text-sm font-medium text-[var(--c-danger)]">Mizan DENK DEĞİL</p>
                <p className="text-xs text-[var(--c-ink-soft)]">
                  Borç {money(tb.data.totalDebit)} · alacak {money(tb.data.totalCredit)}. Her fiş
                  kendi içinde dengeli olsa bile mizan denksiz olabilir; rakamlara güvenmeden önce
                  sebebi bulunmalı.
                </p>
              </div>
            )}

            <Table>
              <thead>
                <tr>
                  <Th>Hesap</Th>
                  <Th>Ad</Th>
                  <Th align="right">Borç</Th>
                  <Th align="right">Alacak</Th>
                  <Th align="right">Bakiye</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {tb.data.rows.map((r) => (
                  <tr key={r.accountCode}>
                    <Td className="font-mono text-xs">{r.accountCode}</Td>
                    <Td>
                      {r.name.tr}
                      <span className="block text-xs text-[var(--c-ink-faint)]">
                        sınıf {r.accountClass} · {SIGN_HINT[r.type]}
                      </span>
                    </Td>
                    <Td align="right">{money(r.debit)}</Td>
                    <Td align="right">{money(r.credit)}</Td>
                    <Td align="right">
                      <span className="font-medium">{money(r.balance)}</span>
                    </Td>
                    <Td>
                      <Button
                        variant="ghost"
                        onClick={() =>
                          setOpenAccount((prev) => (prev === r.accountCode ? null : r.accountCode))
                        }
                      >
                        {openAccount === r.accountCode ? 'Kapat' : 'Hareketler'}
                      </Button>
                    </Td>
                  </tr>
                ))}
                <tr>
                  <Td> </Td>
                  <Td>
                    <span className="font-semibold">Toplam</span>
                  </Td>
                  <Td align="right">
                    <span className="font-semibold">{money(tb.data.totalDebit)}</span>
                  </Td>
                  <Td align="right">
                    <span className="font-semibold">{money(tb.data.totalCredit)}</span>
                  </Td>
                  <Td align="right">
                    <Badge tone={tb.data.balanced ? 'ok' : 'danger'}>
                      {tb.data.balanced ? 'denk' : 'DENK DEĞİL'}
                    </Badge>
                  </Td>
                  <Td> </Td>
                </tr>
              </tbody>
            </Table>
          </>
        )}
      </Card>

      {openAccount !== null && effectiveYear !== null && (
        <LedgerCard accountCode={openAccount} fiscalYear={effectiveYear} />
      )}
    </>
  );
}

function LedgerCard({ accountCode, fiscalYear }: { accountCode: string; fiscalYear: number }) {
  const ledger = useAccountLedger(accountCode, fiscalYear);

  return (
    <Card className="mt-4">
      <CardHeader
        title={`Defter-i kebir — ${accountCode}`}
        description={ledger.data?.name.tr ?? ''}
      />
      {ledger.isPending ? (
        <div className="px-4 py-6">
          <Spinner />
        </div>
      ) : ledger.isError ? (
        <ErrorState message={errorMessage(ledger.error)} />
      ) : (ledger.data?.movements.length ?? 0) === 0 ? (
        <EmptyState title="Hareket yok" />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Fiş</Th>
              <Th>Tarih</Th>
              <Th>Açıklama</Th>
              <Th align="right">Borç</Th>
              <Th align="right">Alacak</Th>
              <Th align="right">Bakiye</Th>
            </tr>
          </thead>
          <tbody>
            {ledger.data?.movements.map((m, i) => (
              <tr key={`${m.entryId}-${i}`}>
                <Td className="font-mono text-xs">
                  {m.entryNumber ?? '—'}
                  {/*
                    ⚠ TERS ÇEVRİLMİŞ FİŞ GİZLENMEZ, İŞARETLENİR. Defterden satır
                    saklamak, yasal defterin ne olduğunu yanlış anlatmak olurdu.
                  */}
                  {m.status === 'REVERSED' && (
                    <span className="ml-1">
                      <Badge tone="warn">ters çevrildi</Badge>
                    </span>
                  )}
                </Td>
                <Td>{m.entryDate}</Td>
                <Td>
                  {m.description}
                  {m.lineDescription && (
                    <span className="block text-xs text-[var(--c-ink-faint)]">
                      {m.lineDescription}
                    </span>
                  )}
                </Td>
                <Td align="right">{money(m.debit)}</Td>
                <Td align="right">{money(m.credit)}</Td>
                <Td align="right">{money(m.runningBalance)}</Td>
              </tr>
            ))}
            <tr>
              <Td> </Td>
              <Td> </Td>
              <Td>
                <span className="font-semibold">Toplam</span>
              </Td>
              <Td align="right">
                <span className="font-semibold">{money(ledger.data?.totalDebit ?? '0.00')}</span>
              </Td>
              <Td align="right">
                <span className="font-semibold">{money(ledger.data?.totalCredit ?? '0.00')}</span>
              </Td>
              <Td align="right">
                <span className="font-semibold">
                  {money(ledger.data?.closingBalance ?? '0.00')}
                </span>
              </Td>
            </tr>
          </tbody>
        </Table>
      )}
    </Card>
  );
}
